import './ceSelection.less';

import React, { useCallback, useEffect } from 'react';
import { AntSelect } from '../../../ant/ant-select/antSelect';
import { FieldWrapper } from './fieldWrapper';
import { FieldProps } from './types';
import { FormBuilderContext } from '../formBuilderContext';
import { observer } from 'mobx-react';
import { fieldNamesTextValue } from 'controls/react/ant/ant-select/types';
import { Section } from 'controls/react/layout/section';
import AddAsset from 'controls/react/windows/addAsset';
import LocalEventsManager from 'core/localEventsManager';

const b = require('b_').with('ce-selection');

// payload broadcast by the legacy jQuery AssetForm.js window (not part of this React tree) over the
// app-wide LocalEventsManager bus — ported from the old formBuilder's sagas/assetSaved.js
interface AssetSavedEvent {
	asset?: { id: string; accountId: string; name?: string };
	lastSelectedTarget?: { id: string };
}

export const CESelection = observer(({ property }: FieldProps) => {
	const store = React.useContext(FormBuilderContext);
	const mode = property.multiSelect ? 'tags' : null;
	const options = (store.dataSources.get(property.id) || []) as { text: string; value: string }[];

	// the assetId field gets an inline "create new asset" button — ported from the old formBuilder's
	// commonDropDown.js AssetComponent special-case (assetId was the only CESelection id that added one).
	const isAssetField = property.id === 'assetId';

	useEffect(() => {
		// only the root-level assetId field reacts — one nested inside a CECollection sub-form has its own,
		// unrelated value tree (ported from the old formBuilder's sagas/assetSaved.js). assetId itself is set
		// directly by onCreateNew below; this only covers the sibling fields the create-asset response carries.
		if (!isAssetField || store.rootFormStore) return;
		const onAssetSaved = (e: AssetSavedEvent) => {
			if (e?.asset?.accountId != null) {
				store.setPropertyValueIfExists('assetAccountId', e.asset.accountId);
			}
			if (e?.lastSelectedTarget?.id != null) {
				store.setPropertyValueIfExists('targetId', e.lastSelectedTarget.id, true);
			}
		};
		LocalEventsManager.bind('assetsaved', onAssetSaved);
		return () => LocalEventsManager.unbind('assetsaved', onAssetSaved);
	}, [store, isAssetField]);

	// AddAsset is the same "create new asset" window used by AssetDropDown/AssetTargetDropDown — reuse it
	// here instead of opening AssetForm.jsp by hand.
	const onCreateNew = useCallback(
		(newAssetId: string) => {
			store.loadDatasource(property, true);
			store.onPropertyValueChange(property, newAssetId);
		},
		[property, store],
	);

	const select = <AntSelect mode={mode} fieldNames={fieldNamesTextValue} options={options} />;

	if (!isAssetField) {
		return <FieldWrapper property={property}>{select}</FieldWrapper>;
	}

	return (
		<Section appearance={'none'} direction={'row'} contentClass={b('content')}>
			<FieldWrapper property={property}>{select}</FieldWrapper>
			<AddAsset containerClass={b('add')} accountId={store.value[property.dependsOn]} onCreateNew={onCreateNew} />
		</Section>
	);
});
